import * as React from 'react'
import I18n from '../../../../core/i18n'
import styled from 'styled-components'
import * as constants from '../../../../static/constants'
import TextClamp from 'components/atoms/TextClamp'
import { utilService } from 'core/services'
import { ICustomPost } from 'static/interfaces'
import { getAgeLabelOnPrice, getAges } from 'utils/reservation'
import { ReservationSelectedDatesDate } from './atoms/ReservationSelectedDatesDate'
import moment from 'moment'
import SvgIcon from 'components/atoms/SvgIcon'
import { getReservationStorageData } from 'utils/reservationSessionStorage'

interface IProps {
  getParticipantTypePrice: (type: string) => number
  getTotalParticipantTypePrice: (quantity: number, price: number) => number | ''
  setShowRefundInfoModal: (value: boolean) => void
  post: ICustomPost
  memberPost: ICustomPost
  cancelPolicy: string
  chargePrice: number
  hasCoupon: boolean
  isPostInFamily: boolean
  guests: {
    adults: number
    children: number
    infants: number
  }
  createdReservation: any
}

export const ReservationDetailInfo: React.VFC<IProps> = ({
  getParticipantTypePrice,
  getTotalParticipantTypePrice,
  setShowRefundInfoModal,
  chargePrice,
  post,
  memberPost,
  cancelPolicy,
  guests,
  isPostInFamily,
  hasCoupon,
  createdReservation,
}) => {
  const ages = getAges(memberPost.post_prices)
  const totalPrice = React.useMemo(() => {
    if (memberPost.post_type === 'private') {
      return getParticipantTypePrice('adult')
    }
    const adultsPrice = getTotalParticipantTypePrice(
      guests.adults,
      getParticipantTypePrice('adult')
    )
    const childrenPrice = getTotalParticipantTypePrice(
      guests.children,
      getParticipantTypePrice('child')
    )
    const infantsPrice = getTotalParticipantTypePrice(
      guests.infants,
      getParticipantTypePrice('infant')
    )
    return (
      (adultsPrice !== '' ? adultsPrice : 0) +
      (childrenPrice !== '' ? childrenPrice : 0) +
      (infantsPrice !== '' ? infantsPrice : 0)
    )
  }, [
    memberPost.post_type,
    guests.adults,
    guests.children,
    guests.infants,
    getParticipantTypePrice,
  ])

  const reservationStorageData = getReservationStorageData()

  const reservation = {
    ...reservationStorageData?.reservation,
    selectedDate: moment(reservationStorageData?.reservation?.selectedDate),
  }
  const resultChargePrice = React.useMemo(() => {
    return createdReservation
      ? utilService.formatPriceToNumber(createdReservation.charge_price)
      : chargePrice
  }, [chargePrice, createdReservation])

  const postImage =
    memberPost.post_images[0]?.large_url || memberPost.post_images[0]?.image_url || null
  const getFormattedParticipantTypePrice = (type: string) => {
    switch (type) {
      case 'adult':
        return utilService.formatPrice(
          getTotalParticipantTypePrice(guests.adults, getParticipantTypePrice('adult')) || 0
        )

      case 'child':
        return utilService.formatPrice(
          getTotalParticipantTypePrice(guests.children, getParticipantTypePrice('child')) || 0
        )
      case 'infant':
        return utilService.formatPrice(
          getTotalParticipantTypePrice(guests.infants, getParticipantTypePrice('infant')) || 0
        )
      default:
        return utilService.formatPrice(0)
    }
  }
  return (
    <ReservationDetailWrapper>
      <div className="dateTimeBox">
        <ReservationSelectedDatesDate
          date={{ date: reservation.selectedDate }}
          startTime={reservation.departure}
          post={memberPost}
          fontSize={16}
        />
      </div>
      <div className="topBox">
        <div className="thumb">
          {postImage && <img src={postImage ?? `${constants.ASSET_HOST}/images/no-image.png`} />}
        </div>
        <div className="boxContent">
          {isPostInFamily && <p className="parentTourName">{memberPost.name}</p>}
          <p className="tourName"> {isPostInFamily ? post.name : memberPost.name}</p>
        </div>
      </div>

      <div className="tourDetailItem">
        <h6 className="title">{I18n.t('reservation.reservation_meeting_point')}</h6>
        <div className="detailContent">
          {memberPost.meeting_points.length > 0 && memberPost.meeting_points[0] && (
            <a
              className="meetingPointBox"
              href={`https://www.google.com/maps?q=${memberPost?.meeting_points[0].latitude},${memberPost?.meeting_points[0].longitude}`}
              target="_blank"
            >
              <SvgIcon name="place_filled" className="icon" />
              {memberPost.meeting_points[0] ? `${memberPost.meeting_points[0].name}` : ''}
            </a>
          )}
        </div>
      </div>

      <div className="tourDetailItem">
        <h6 className="title">{I18n.t('reservation.reservation_price_breakdown')}</h6>
        <div className="detailContent">
          {memberPost.post_type === 'group' ? (
            <>
              <div className="rowItem">
                <span className="label">
                  Adults {getAgeLabelOnPrice('Adults', ages)} <strong>{guests.adults}</strong> ×{' '}
                  {utilService.formatPrice(getParticipantTypePrice('adult'))}
                </span>
                <span className="value">{getFormattedParticipantTypePrice('adult')}</span>
              </div>

              {guests.children > 0 && (
                <div className="rowItem">
                  <span className="label">
                    Children {getAgeLabelOnPrice('Children', ages)}
                    <strong>{guests.children}</strong> ×{' '}
                    {utilService.formatPrice(getParticipantTypePrice('child'))}
                  </span>
                  <span className="value">{getFormattedParticipantTypePrice('child')}</span>
                </div>
              )}

              {guests.infants > 0 && (
                <div className="rowItem">
                  <span className="label">
                    Infants {getAgeLabelOnPrice('Infants', ages)} <strong>{guests.infants}</strong>{' '}
                    × {utilService.formatPrice(getParticipantTypePrice('infant'))}
                  </span>
                  <span className="value">{getFormattedParticipantTypePrice('infant')}</span>
                </div>
              )}
            </>
          ) : (
            <div className="rowItem">
              <span className="label">
                Guests <strong>{guests.adults}</strong>
              </span>
            </div>
          )}
        </div>

        {hasCoupon && (
          <div className="rowItem">
            <span className="label">Coupon</span>
            <span className="value">
              - {utilService.formatPrice(totalPrice - resultChargePrice)}
            </span>
          </div>
        )}
      </div>

      <div className="tourDetailItem">
        <h6 className="title no-mb">{I18n.t('generic.total_price')}</h6>
        <div className="detailContent">
          <div>
            <strong>
              ¥
              <span className="chargePrice">{utilService.separate(String(resultChargePrice))}</span>
            </strong>
          </div>
          <span className="priceNote">{I18n.t('reservation.reservation_tax_notice')}</span>
        </div>
      </div>

      <div className="tourDetailItem">
        <h6 className="title">{I18n.t('reservation.reservation_cancellation_policy')}</h6>
        <div className="detailContent cancelPolicy">
          <TextClamp line={2}>{cancelPolicy}</TextClamp>
          <button
            type="button"
            className="showMoreBtn"
            onClick={() => {
              setShowRefundInfoModal(true)
            }}
          >
            {I18n.t('generic.show_more')}
          </button>
        </div>
      </div>
    </ReservationDetailWrapper>
  )
}

const ReservationDetailWrapper = styled.div`
  width: 100%;
  max-width: 48rem;
  position: sticky;
  top: 4.4rem;
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 0.8rem;
  padding: 0 1.6rem;
  @media screen and (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
    position: unset;
    top: auto;
    margin: 0 auto;
  }
  @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    max-width: unset;
    position: relative;
    top: auto;
  }
  .dateTimeBox {
    padding: 1.6rem 1.6rem 0;
    > div {
      padding: 1.2rem 1.6rem;
      flex-wrap: wrap;
      background-color: ${constants.COLORS.useallyBG};
      border-radius: 0.4rem;
    }
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding: 1.6rem 0 0;
      > div {
        padding: 0.8rem 1.2rem;
        font-size: 1.5rem;
      }
    }
  }
  .topBox {
    display: flex;
    flex-wrap: wrap;
    padding: 1.6rem 0;
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding: 1.2rem 0;
    }
    .thumb {
      width: 40%;
      max-width: 20rem;
      overflow: hidden;
      @media screen and (max-width: ${constants.BREAKPOINT_DESKTOP_LARGE}px) {
        max-width: 12rem;
      }
      img {
        width: 100%;
        height: auto;
        border-radius: 0.8rem;
        aspect-ratio: 4 / 3;
        object-fit: cover;
      }
    }

    .boxContent {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      justify-content: center;
      padding-left: 1.6rem;
      width: 60%;
      @media screen and (max-width: ${constants.BREAKPOINT_DESKTOP_LARGE}px) {
        width: calc(100% - 12rem);
      }
    }

    .parentTourName {
      margin-bottom: 0.8rem;
      color: ${constants.COLORS.black};
      font-size: 1.6rem;
      font-weight: 700;
      line-height: 2.4rem;
      text-transform: capitalize;
      @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        line-height: 2rem;
        font-size: 1.4rem;
      }
    }

    .tourName {
      color: ${constants.COLORS.black};
      font-size: 1.5rem;
      font-weight: 400;
      line-height: 2rem;
      @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        font-size: 1.3rem;
      }
    }
  }

  .meetingPointBox {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    color: ${constants.COLORS.primary};
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    text-decoration: underline;
    .icon {
      width: 2rem;
      height: 2rem;
      margin-right: 0.4rem;
    }
    &:hover {
      text-decoration: none;
    }
  }

  .tourDetailItem {
    border-top: 1px solid ${constants.COLORS.divider2};
    padding: 1.6rem 0;
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding: 1.2rem 0;
    }
    h6 {
      margin-bottom: 0.4rem;
      color: ${constants.COLORS.subText};
      font-size: 14px;
      font-weight: 700;
      line-height: 24px;
      text-transform: capitalize;
      &.no-mb {
        margin-bottom: 0;
      }
    }
    .detailContent {
      &.cancelPolicy {
        > div {
          font-size: 1.4rem;
        }
      }
      p {
        color: ${constants.COLORS.black};
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
      }
      .chargePrice {
        font-size: 20px;
      }
    }
    .rowItem {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
    }
    .priceNote {
      display: block;
      font-size: 1rem;
    }
  }

  .showMoreBtn {
    padding: 0;
    border: 0;
    background: none;
    color: ${constants.COLORS.primary};
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    text-decoration: underline;
    text-transform: capitalize;
    cursor: pointer;
    &:hover {
      text-decoration: none;
    }
  }
`
